import React, { useState, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const token = Cookies.get('token');
  const userName = Cookies.get('userName');
  const [isFilmsDropdownOpen, setIsFilmsDropdownOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const filmsDropdownTimer = useRef(null);
  const profileDropdownTimer = useRef(null);

  const handleLogout = () => {
    Cookies.remove('token');
    Cookies.remove('userName');
    Cookies.remove('userRole');
    navigate('/login');
  };

  // Check if the current path matches the link (updated to handle nested routes)
  const isActive = (path) => {
    // For Reports - check if we're on /reports, /reports/new, or /reports/edit/:id
    if (path === '/reports') {
      return location.pathname === '/reports' ||
        location.pathname === '/reports/new' ||
        location.pathname.startsWith('/reports/edit');
    }

    // For Custom Reports - check if we're on /custom-reports or /custom-reports/new-custom-report or edit
    if (path === '/custom-reports') {
      return location.pathname === '/custom-reports' ||
        location.pathname === '/custom-reports/new-custom-report' ||
        location.pathname.startsWith('/custom-reports/edit');
    }

    // For Invoices - check if we're on /invoices, /invoices/new, /invoices/edit/:id, or /invoices/view/:id
    if (path === '/invoices') {
      return location.pathname === '/invoices' ||
        location.pathname === '/invoices/new' ||
        location.pathname.startsWith('/invoices/edit') ||
        location.pathname.startsWith('/invoices/view');
    }

    // For all other routes, exact match
    return location.pathname === path;
  };

  // Check if any Films submenu is active
  const isFilmsActive = () => {
    return location.pathname.startsWith('/films');
  };

  // Check if any Profile submenu is active
  const isProfileActive = () => {
    return location.pathname === '/profile' || location.pathname === '/change-password';
  };

  // Films dropdown handlers
  const handleFilmsMouseEnter = () => {
    if (filmsDropdownTimer.current) {
      clearTimeout(filmsDropdownTimer.current);
    }
    setIsFilmsDropdownOpen(true);
  };

  const handleFilmsMouseLeave = () => {
    filmsDropdownTimer.current = setTimeout(() => {
      setIsFilmsDropdownOpen(false);
    }, 200);
  };

  // Profile dropdown handlers
  const handleProfileMouseEnter = () => {
    if (profileDropdownTimer.current) {
      clearTimeout(profileDropdownTimer.current);
    }
    setIsProfileDropdownOpen(true);
  };

  const handleProfileMouseLeave = () => {
    profileDropdownTimer.current = setTimeout(() => {
      setIsProfileDropdownOpen(false);
    }, 200);
  };

  // Toggle mobile films dropdown
  const toggleMobileFilmsDropdown = () => {
    setIsFilmsDropdownOpen(!isFilmsDropdownOpen);
  };

  // Toggle mobile profile dropdown
  const toggleMobileProfileDropdown = () => {
    setIsProfileDropdownOpen(!isProfileDropdownOpen);
  };

  // Close mobile menu when link is clicked
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setIsFilmsDropdownOpen(false);
    setIsProfileDropdownOpen(false);
  };

  return (
    <nav className="bg-gradient-to-r from-indigo-900 to-indigo-900 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="text-white text-2xl font-bold hover:text-gray-200 transition">
              ρηѕ
            </Link>
          </div>

          {/* Desktop Navigation Links */}
          <div className="hidden lg:flex items-center space-x-4">
            {!token ? (
              <React.Fragment>
                {/* Login/Signup links if needed */}
              </React.Fragment>
            ) : (
              <React.Fragment>
                <Link
                  to="/dashboard"
                  className={`${isActive('/dashboard')
                    ? 'bg-white text-blue-600 font-bold'
                    : 'text-white hover:bg-white hover:text-blue-600'
                    } px-4 py-2 rounded-md text-sm font-medium transition duration-300`}
                >
                  DASHBOARD
                </Link>

                <Link
                  to="/employees"
                  className={`${isActive('/employees')
                    ? 'bg-white text-blue-600 font-bold'
                    : 'text-white hover:bg-white hover:text-blue-600'
                    } px-4 py-2 rounded-md text-sm font-medium transition duration-300`}
                >
                  EMPLOYEES
                </Link>

                <Link
                  to="/customers"
                  className={`${isActive('/customers')
                    ? 'bg-white text-blue-600 font-bold'
                    : 'text-white hover:bg-white hover:text-blue-600'
                    } px-4 py-2 rounded-md text-sm font-medium transition duration-300`}
                >
                  CUSTOMERS
                </Link>

                {/* Others Dropdown */}
                <div
                  className="relative"
                  onMouseEnter={handleFilmsMouseEnter}
                  onMouseLeave={handleFilmsMouseLeave}
                >
                  <button
                    className={`${isFilmsActive()
                      ? 'bg-white text-blue-600 font-bold'
                      : 'text-white hover:bg-white hover:text-blue-600'
                      } px-4 py-2 rounded-md text-sm font-medium transition duration-300 flex items-center`}
                  >
                    OTHERS
                    <svg
                      className={`ml-2 h-4 w-4 transition-transform duration-200 ${isFilmsDropdownOpen ? 'rotate-180' : ''
                        }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Others Dropdown Menu */}
                  {isFilmsDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl py-2 z-50">
                      <Link
                        to="/films/film-rates"
                        className={`${isActive('/films/film-rates')
                          ? 'bg-blue-100 text-blue-600 font-semibold'
                          : 'text-gray-700 hover:bg-gray-100'
                          } block px-4 py-2 text-sm transition duration-200`}
                      >
                        🏷️ FILM RATES
                      </Link>
                      <Link
                        to="/films/film-size"
                        className={`${isActive('/films/film-size')
                          ? 'bg-blue-100 text-blue-600 font-semibold'
                          : 'text-gray-700 hover:bg-gray-100'
                          } block px-4 py-2 text-sm transition duration-200`}
                      >
                        📏 FILM SIZES
                      </Link>
                      <Link
                        to="/films/observation-and-remarks"
                        className={`${isActive('/films/observation-and-remarks')
                          ? 'bg-blue-100 text-blue-600 font-semibold'
                          : 'text-gray-700 hover:bg-gray-100'
                          } block px-4 py-2 text-sm transition duration-200`}
                      >
                        📝 OBS & REMARKS
                      </Link>
                    </div>
                  )}
                </div>

                <Link
                  to="/reports"
                  className={`${isActive('/reports')
                    ? 'bg-white text-blue-600 font-bold'
                    : 'text-white hover:bg-white hover:text-blue-600'
                    } px-4 py-2 rounded-md text-sm font-medium transition duration-300`}
                >
                  REPORTS
                </Link>

                <Link
                  to="/custom-reports"
                  className={`${isActive('/custom-reports')
                    ? 'bg-white text-blue-600 font-bold'
                    : 'text-white hover:bg-white hover:text-blue-600'
                    } px-4 py-2 rounded-md text-sm font-medium transition duration-300`}
                >
                  CUSTOM REPORTS
                </Link>

                <Link
                  to="/invoices"
                  className={`${isActive('/invoices')
                    ? 'bg-white text-blue-600 font-bold'
                    : 'text-white hover:bg-white hover:text-blue-600'
                    } px-4 py-2 rounded-md text-sm font-medium transition duration-300`}
                >
                  INVOICES
                </Link>

                {/* Profile Dropdown */}
                <div
                  className="relative"
                  onMouseEnter={handleProfileMouseEnter}
                  onMouseLeave={handleProfileMouseLeave}
                >
                  <button
                    className={`${isProfileActive()
                      ? 'bg-white text-blue-600 font-bold'
                      : 'text-white hover:bg-white hover:text-blue-600'
                      } px-4 py-2 rounded-md text-sm font-medium transition duration-300 flex items-center space-x-2`}
                  >
                    <span className="w-8 h-8 bg-white text-blue-600 rounded-full flex items-center justify-center font-bold">
                      {userName ? userName.charAt(0).toUpperCase() : 'U'}
                    </span>
                    <span>{userName}</span>
                    <svg
                      className={`h-4 w-4 transition-transform duration-200 ${isProfileDropdownOpen ? 'rotate-180' : ''
                        }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Profile Dropdown Menu */}
                  {isProfileDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-2xl border border-gray-200 py-1 z-50 animate-fade-in">
                      {/* User Info Section */}
                      <div className="px-4 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-lg shadow-md">
                            {userName.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-gray-900 truncate">{userName}</p>
                            <p className="text-xs text-gray-600 mt-0.5 flex items-center">
                              {Cookies.get('userRole') === 'admin' ? (
                                <React.Fragment>
                                  <svg className="w-3 h-3 mr-1 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
                                    <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
                                  </svg>
                                  Administrator
                                </React.Fragment>
                              ) : (
                                <React.Fragment>
                                  <svg className="w-3 h-3 mr-1 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                  </svg>
                                  Employee
                                </React.Fragment>
                              )}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Menu Items */}
                      <div className="py-1">
                        <Link
                          to="/change-password"
                          className={`${isActive('/change-password')
                            ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-600'
                            : 'text-gray-700 hover:bg-gray-50 border-l-4 border-transparent'
                            } group flex items-center px-4 py-2.5 text-sm font-medium transition-all duration-200`}
                        >
                          <svg className={`w-5 h-5 mr-3 ${isActive('/change-password') ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                          </svg>
                          <span>Change Password</span>
                        </Link>

                        <div className="border-t border-gray-100 my-1"></div>

                        <button
                          onClick={() => {
                            if (window.confirm('Are you sure you want to logout?')) {
                              handleLogout();
                            }
                          }}
                          className="w-full group flex items-center px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 border-l-4 border-transparent hover:border-red-600 transition-all duration-200"
                        >
                          <svg className="w-5 h-5 mr-3 text-red-500 group-hover:text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                          <span>Logout</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </React.Fragment>
            )}
          </div>

          {/* Mobile Hamburger Button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-white hover:text-gray-200 focus:outline-none focus:text-gray-200 transition"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {isMobileMenuOpen ? (
                  <path d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-gradient-to-r from-red-600 to-purple-700">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {!token ? (
              <React.Fragment>
                {/* Login/Signup links if needed */}
              </React.Fragment>
            ) : (
              <React.Fragment>
                <Link
                  to="/dashboard"
                  onClick={closeMobileMenu}
                  className={`${isActive('/dashboard')
                    ? 'bg-white text-blue-600 font-bold'
                    : 'text-white hover:bg-white hover:text-blue-600'
                    } block px-3 py-2 rounded-md text-base font-medium transition duration-300`}
                >
                  DASHBOARD
                </Link>

                <Link
                  to="/employees"
                  onClick={closeMobileMenu}
                  className={`${isActive('/employees')
                    ? 'bg-white text-blue-600 font-bold'
                    : 'text-white hover:bg-white hover:text-blue-600'
                    } block px-3 py-2 rounded-md text-base font-medium transition duration-300`}
                >
                  EMPLOYEES
                </Link>

                <Link
                  to="/customers"
                  onClick={closeMobileMenu}
                  className={`${isActive('/customers')
                    ? 'bg-white text-blue-600 font-bold'
                    : 'text-white hover:bg-white hover:text-blue-600'
                    } block px-3 py-2 rounded-md text-base font-medium transition duration-300`}
                >
                  CUSTOMERS
                </Link>

                {/* Mobile Others Dropdown */}
                <div>
                  <button
                    onClick={toggleMobileFilmsDropdown}
                    className={`${isFilmsActive()
                      ? 'bg-white text-blue-600 font-bold'
                      : 'text-white hover:bg-white hover:text-blue-600'
                      } w-full text-left px-3 py-2 rounded-md text-base font-medium transition duration-300 flex items-center justify-between`}
                  >
                    <span>OTHERS</span>
                    <svg
                      className={`h-5 w-5 transition-transform duration-200 ${isFilmsDropdownOpen ? 'rotate-180' : ''
                        }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {isFilmsDropdownOpen && (
                    <div className="pl-4 space-y-1 mt-1">
                      <Link
                        to="/films/film-rates"
                        onClick={closeMobileMenu}
                        className={`${isActive('/films/film-rates')
                          ? 'bg-white text-blue-600 font-semibold'
                          : 'text-white hover:bg-white hover:text-blue-600'
                          } block px-3 py-2 rounded-md text-sm transition duration-300`}
                      >
                        🏷️ FILM RATES
                      </Link>
                      <Link
                        to="/films/film-size"
                        onClick={closeMobileMenu}
                        className={`${isActive('/films/film-size')
                          ? 'bg-white text-blue-600 font-semibold'
                          : 'text-white hover:bg-white hover:text-blue-600'
                          } block px-3 py-2 rounded-md text-sm transition duration-300`}
                      >
                        📏 FILM SIZES
                      </Link>
                      <Link
                        to="/films/observation-and-remarks"
                        onClick={closeMobileMenu}
                        className={`${isActive('/films/observation-and-remarks')
                          ? 'bg-white text-blue-600 font-semibold'
                          : 'text-white hover:bg-white hover:text-blue-600'
                          } block px-3 py-2 rounded-md text-sm transition duration-300`}
                      >
                        📝 OBS & REMARKS
                      </Link>
                    </div>
                  )}
                </div>

                <Link
                  to="/reports"
                  onClick={closeMobileMenu}
                  className={`${isActive('/reports')
                    ? 'bg-white text-blue-600 font-bold'
                    : 'text-white hover:bg-white hover:text-blue-600'
                    } block px-3 py-2 rounded-md text-base font-medium transition duration-300`}
                >
                  REPORTS
                </Link>

                <Link
                  to="/custom-reports"
                  onClick={closeMobileMenu}
                  className={`${isActive('/custom-reports')
                    ? 'bg-white text-blue-600 font-bold'
                    : 'text-white hover:bg-white hover:text-blue-600'
                    } block px-3 py-2 rounded-md text-base font-medium transition duration-300`}
                >
                  CUSTOM REPORTS
                </Link>

                <Link
                  to="/invoices"
                  onClick={closeMobileMenu}
                  className={`${isActive('/invoices')
                    ? 'bg-white text-blue-600 font-bold'
                    : 'text-white hover:bg-white hover:text-blue-600'
                    } block px-3 py-2 rounded-md text-base font-medium transition duration-300`}
                >
                  INVOICES
                </Link>

                {/* Mobile Profile Section */}
                <div className="border-t border-white/20 pt-2 mt-2">
                  <button
                    onClick={toggleMobileProfileDropdown}
                    className={`${isProfileActive()
                      ? 'bg-white text-blue-600 font-bold'
                      : 'text-white hover:bg-white hover:text-blue-600'
                      } w-full text-left px-3 py-2 rounded-md text-base font-medium transition duration-300 flex items-center justify-between`}
                  >
                    <div className="flex items-center space-x-2">
                      <span className="w-8 h-8 bg-white text-blue-600 rounded-full flex items-center justify-center font-bold text-sm">
                        {userName ? userName.charAt(0).toUpperCase() : 'U'}
                      </span>
                      <span>{userName}</span>
                    </div>
                    <svg
                      className={`h-5 w-5 transition-transform duration-200 ${isProfileDropdownOpen ? 'rotate-180' : ''
                        }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {isProfileDropdownOpen && (
                    <div className="pl-4 space-y-1 mt-1">
                      <div className="px-3 py-2 text-xs text-white/80">
                        {Cookies.get('userRole') === 'admin' ? '👑 Administrator' : '👤 Employee'}
                      </div>

                      <Link
                        to="/change-password"
                        onClick={closeMobileMenu}
                        className={`${isActive('/change-password')
                          ? 'bg-white text-blue-600 font-semibold'
                          : 'text-white hover:bg-white hover:text-blue-600'
                          } block px-3 py-2 rounded-md text-sm transition duration-300`}
                      >
                        🔐 Change Password
                      </Link>

                      <button
                        onClick={() => {
                          if (window.confirm('Are you sure you want to logout?')) {
                            handleLogout();
                            closeMobileMenu();
                          }
                        }}
                        className="w-full text-left px-3 py-2 rounded-md text-sm text-white hover:bg-red-500 transition duration-300"
                      >
                        🚪 Logout
                      </button>
                    </div>
                  )}
                </div>
              </React.Fragment>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;